import { inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { safeLocalStorage } from './branding-cache';
import { SEASON_PERSISTENCE_ENABLED } from './season-persistence.token';
import { TenantContextService } from './tenant-context.service';
import { TenantFeaturesService } from './tenant-features.service';

const START_YEAR = 2026;

/** Prefix of the per-tenant localStorage key holding the selected season. */
const STORAGE_KEY_PREFIX = 'sp_season_';

@Injectable({
  providedIn: 'root',
})
export class SeasonManagerService {
  private readonly tenantFeatures = inject(TenantFeaturesService);
  private readonly tenantContext = inject(TenantContextService);

  /**
   * Opt-in, per app. `false` by default: only the admin panel persists the
   * selection (see `season-persistence.token.ts`). When disabled, this service
   * behaves exactly as it did before persistence existed — nothing is read,
   * written or restored, and `season$` re-emits as it always did.
   */
  private readonly persistenceEnabled = inject(SEASON_PERSISTENCE_ENABLED);

  /**
   * `localStorage` or `null` when it is not usable (sandboxed iframe, opaque
   * origin, site data blocked) or when persistence is off. Reused from
   * `branding-cache` — merely evaluating the `localStorage` property can throw
   * `SecurityError`.
   */
  private readonly storage = this.persistenceEnabled ? safeLocalStorage() : null;

  private seasonChangeMonth = 6;

  /**
   * In-memory mirror of the persisted selection, keyed by tenantId.
   * Also the ONLY source of truth when `storage` is null, so that an explicit
   * user choice still survives the `seasonChangeMonth$` emissions.
   */
  private readonly selectionByTenant = new Map<string, string>();

  /** Tenant the current selection belongs to (null before auth resolves). */
  private activeTenantId: string | null = null;

  private seasonSubject = new BehaviorSubject<string>(this.getActualSeason());
  public season$ = this.seasonSubject.asObservable();

  constructor() {
    // Restore the selection saved for the active tenant. `tenantId$` is backed
    // by a BehaviorSubject, so when the tenant is already known this runs
    // synchronously here, during construction.
    if (this.persistenceEnabled) {
      this.tenantContext.tenantId$.subscribe((tenantId) => {
        this.applyTenant(tenantId);
      });
    }

    // Keep the tenant's season-change month up to date (consumed by
    // `getSeasonChangeMonth()`), but only recalculate the selected season when
    // the user has NOT made a choice for this tenant: an explicit selection
    // (or the one restored from a previous session) always wins.
    this.tenantFeatures.seasonChangeMonth$.subscribe((month) => {
      this.seasonChangeMonth = month;
      if (this.hasSelection()) return;
      this.emit(this.getActualSeason());
    });
  }

  setSeason(season: string): void {
    if (this.persistenceEnabled && this.activeTenantId) {
      this.selectionByTenant.set(this.activeTenantId, season);
      this.write(this.activeTenantId, season);
    }
    this.emit(season);
  }

  getSeason(): string {
    return this.seasonSubject.getValue();
  }

  /** Tenant's cached 0-indexed season-change month (default 6 = July). */
  getSeasonChangeMonth(): number {
    return this.seasonChangeMonth;
  }

  availableSeasons(): string[] {
    const actualYear = new Date().getFullYear();
    const seasons: string[] = [];
    for (let year = START_YEAR; year <= actualYear + 1; year++) {
      seasons.push(`${year - 1}-${year}`);
    }
    return seasons;
  }

  getActualSeason(): string {
    const date = new Date();
    const month = date.getMonth();
    const year = date.getFullYear();
    return month < this.seasonChangeMonth ? `${year - 1}-${year}` : `${year}-${year + 1}`;
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  /**
   * Switches the active tenant: the selection becomes the one saved for THAT
   * tenant, or the calculated season when it has none. The previous tenant's
   * choice is never carried over (each tenant may have its own
   * `seasonChangeMonth` and its own set of seasons).
   */
  private applyTenant(tenantId: string): void {
    this.activeTenantId = tenantId;
    const stored = this.read(tenantId);
    this.emit(stored ?? this.getActualSeason());
  }

  /** True when the user has an explicit choice for the active tenant. */
  private hasSelection(): boolean {
    if (!this.persistenceEnabled) return false;
    if (!this.activeTenantId) return false;
    return this.read(this.activeTenantId) !== null;
  }

  /**
   * Reads the saved selection, validating it against `availableSeasons()`:
   * that list grows with time, so a value saved long ago can become stale.
   * A stale value is dropped from storage and ignored.
   */
  private read(tenantId: string): string | null {
    const cached = this.selectionByTenant.get(tenantId);
    const value = cached ?? this.getItem(tenantId);
    if (!value) return null;
    if (!this.availableSeasons().includes(value)) {
      this.selectionByTenant.delete(tenantId);
      this.removeItem(tenantId);
      return null;
    }
    this.selectionByTenant.set(tenantId, value);
    return value;
  }

  /**
   * With persistence OFF this is a plain `next()`, exactly as before this
   * feature existed: the subject re-emits even when the string has not changed,
   * and that re-emission is the invalidation signal consumers rely on once the
   * tenant's real `seasonChangeMonth` arrives from Firestore.
   *
   * With persistence ON the redundant emission is suppressed (it fired twice on
   * every boot, re-triggering every `switchMap` hanging off `season$`). See the
   * "deuda aceptada" note in `progress/impl_persist-selected-season-year.md`.
   */
  private emit(season: string): void {
    if (this.persistenceEnabled && this.seasonSubject.getValue() === season) {
      return;
    }
    this.seasonSubject.next(season);
  }

  private key(tenantId: string): string {
    return `${STORAGE_KEY_PREFIX}${tenantId}`;
  }

  private getItem(tenantId: string): string | null {
    if (!this.storage) return null;
    try {
      return this.storage.getItem(this.key(tenantId));
    } catch {
      return null;
    }
  }

  private write(tenantId: string, season: string): void {
    if (!this.storage) return;
    try {
      this.storage.setItem(this.key(tenantId), season);
    } catch {
      /* persisting the season is best-effort — never break the UI for it */
    }
  }

  private removeItem(tenantId: string): void {
    if (!this.storage) return;
    try {
      this.storage.removeItem(this.key(tenantId));
    } catch {
      /* noop */
    }
  }
}
